/**
 * 🛸 CENTRE DE COMMUNICATION INTERSTELLAIRE
 * Section de contact en mode dinguerie spatiale !
 * Design cabine de vaisseau spatial futuriste
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Truck, MessageCircle, Radio, Satellite, Zap, Waves } from 'lucide-react';

const ContactSection = ({ isDarkMode, children, onDeliveryClick, className = '' }) => {
  const [activeChannel, setActiveChannel] = useState(null);
  const [signalStrength, setSignalStrength] = useState([3, 5, 4, 2, 5, 3, 4]);
  
  // Animation des barres de signal
  useEffect(() => {
    const interval = setInterval(() => {
      setSignalStrength(prev => prev.map(() => Math.floor(Math.random() * 5) + 1));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Canaux de communication
  const channels = [
    {
      id: 'voice',
      name: 'Canal Vocal',
      frequency: 'FREQ 07.80.64',
      icon: <Phone className="w-6 h-6" />,
      color: 'cyan',
      action: () => window.location.href = 'tel:0780648976',
      subtitle: '89.76 MHz',
      status: 'ACTIF'
    },
    {
      id: 'instagram',
      name: 'Station Visuelle',
      frequency: 'INSTA-NET',
      icon: <span className="text-2xl">📸</span>,
      color: 'pink',
      action: () => window.open('https://www.instagram.com/la_petite_epicerie.shop/', '_blank'),
      subtitle: '@la_petite_epicerie.shop',
      status: 'NOUVEAU'
    },
    {
      id: 'facebook',
      name: 'Réseau Social',
      frequency: 'FB-LINK',
      icon: <span className="text-2xl">👤</span>,
      color: 'blue',
      action: () => window.open('https://www.facebook.com/profile.php?id=61588543479522', '_blank'),
      subtitle: 'La Petite Épicerie',
      status: 'ACTIF'
    },
    {
      id: 'snap',
      name: 'Portail Spectral',
      frequency: 'GHOST-NET',
      icon: <span className="text-2xl">👻</span>,
      color: 'yellow',
      action: () => window.open('https://www.snapchat.com/@lapetiteepi62', '_blank'),
      subtitle: '@lapetiteepi62',
      status: 'EN LIGNE'
    },
    {
      id: 'delivery',
      name: 'Navette Express',
      frequency: 'MAX-CARGO',
      icon: <Truck className="w-6 h-6" />,
      color: 'purple',
      action: onDeliveryClick,
      subtitle: 'Livraison Warp',
      status: 'DISPONIBLE'
    }
  ];

  const getColorClasses = (color, type) => {
    const colors = {
      cyan: {
        bg: 'from-cyan-500/20 to-cyan-600/10',
        border: 'border-cyan-500/40',
        glow: 'shadow-cyan-500/30',
        text: 'text-cyan-400',
        pulse: 'bg-cyan-400'
      },
      yellow: {
        bg: 'from-yellow-500/20 to-amber-600/10',
        border: 'border-yellow-500/40',
        glow: 'shadow-yellow-500/30',
        text: 'text-yellow-400',
        pulse: 'bg-yellow-400'
      },
      purple: {
        bg: 'from-purple-500/20 to-pink-600/10',
        border: 'border-purple-500/40',
        glow: 'shadow-purple-500/30',
        text: 'text-purple-400',
        pulse: 'bg-purple-400'
      },
      pink: {
        bg: 'from-pink-500/20 to-rose-600/10',
        border: 'border-pink-500/40',
        glow: 'shadow-pink-500/30',
        text: 'text-pink-400',
        pulse: 'bg-pink-400'
      },
      blue: {
        bg: 'from-blue-500/20 to-blue-600/10',
        border: 'border-blue-500/40',
        glow: 'shadow-blue-500/30',
        text: 'text-blue-400',
        pulse: 'bg-blue-400'
      }
    };
    return colors[color][type];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.10, duration: 0.4 }}
      className={`max-w-4xl mx-auto mb-6 ${className}`}
    >
      <div 
        className="relative backdrop-blur-xl rounded-2xl overflow-hidden border border-cyan-500/30"
        style={{
          background: 'linear-gradient(135deg, rgba(10, 20, 40, 0.95) 0%, rgba(5, 15, 35, 0.98) 100%)',
          boxShadow: '0 0 60px rgba(0, 200, 255, 0.15), inset 0 1px 0 rgba(0, 200, 255, 0.1)'
        }}
      >
        {/* Effet scanlines */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 255, 0.1) 2px, rgba(0, 255, 255, 0.1) 4px)',
          }}
        />
        
        {/* Coins HUD */}
        <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-cyan-400/60" />
        <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-cyan-400/60" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-cyan-400/60" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-cyan-400/60" />
        
        {/* Particules flottantes */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-cyan-400/40"
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${20 + Math.random() * 60}%`,
            }}
          />
        ))}

        <div className="relative z-10 p-6">
          {/* Header spatial */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Icône avec animation */}
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.2) 0%, rgba(100, 100, 255, 0.2) 100%)',
                    border: '1px solid rgba(0, 200, 255, 0.3)'
                  }}
                >
                  <Satellite className="w-7 h-7 text-cyan-400" />
                </motion.div>
                {/* Pulse d'activité */}
                <motion.div
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-xl border-2 border-cyan-400"
                />
              </div>
              
              <div>
                <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 flex items-center gap-2">
                  <Radio className="w-5 h-5 text-cyan-400" />
                  CENTRE DE COMMUNICATION
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-cyan-500/70 text-xs font-mono">STATION LPE</span>
                  <span className="text-cyan-400/50">•</span>
                  <span className="text-green-400 text-xs font-mono flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    SIGNAL OPTIMAL
                  </span>
                </div>
              </div>
            </div>
            
            {/* Indicateur de signal animé */}
            <div className="hidden sm:flex items-end gap-0.5 h-8">
              {signalStrength.map((height, i) => (
                <motion.div
                  key={i}
                  animate={{ height: `${height * 20}%` }}
                  transition={{ duration: 0.3 }}
                  className="w-1.5 bg-gradient-to-t from-cyan-500 to-cyan-300 rounded-full"
                  style={{ minHeight: '4px' }}
                />
              ))}
            </div>
          </div>
          
          {/* Grille de canaux de communication */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {channels.map((channel, index) => (
              <motion.button
                key={channel.id}
                onClick={channel.action}
                onMouseEnter={() => setActiveChannel(channel.id)}
                onMouseLeave={() => setActiveChannel(null)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.03, y: -5 }}
                whileTap={{ scale: 0.98 }}
                className={`relative group p-5 rounded-xl border transition-all duration-300
                  bg-gradient-to-br ${getColorClasses(channel.color, 'bg')} 
                  ${getColorClasses(channel.color, 'border')}
                  hover:shadow-lg hover:${getColorClasses(channel.color, 'glow')}`}
                data-testid={`channel-${channel.id}`}
              >
                {/* Effet de survol - lignes de scan */}
                <AnimatePresence>
                  {activeChannel === channel.id && (
                    <motion.div
                      initial={{ top: '0%' }}
                      animate={{ top: '100%' }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8, repeat: Infinity }}
                      className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
                    />
                  )}
                </AnimatePresence>
                
                {/* Indicateur de statut */}
                <div className="absolute top-3 right-3 flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${getColorClasses(channel.color, 'pulse')} animate-pulse`} />
                  <span className={`text-[10px] font-mono ${getColorClasses(channel.color, 'text')}`}>
                    {channel.status}
                  </span>
                </div>
                
                {/* Icône principale */}
                <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 
                  bg-gradient-to-br ${getColorClasses(channel.color, 'bg')} 
                  border ${getColorClasses(channel.color, 'border')}
                  group-hover:scale-110 transition-transform duration-300`}
                >
                  <span className={getColorClasses(channel.color, 'text')}>
                    {channel.icon}
                  </span>
                </div>
                
                {/* Fréquence / Identifiant */}
                <div className="text-center">
                  <div className={`text-xs font-mono mb-1 ${getColorClasses(channel.color, 'text')} opacity-70`}>
                    {channel.frequency}
                  </div>
                  <h4 className="text-white font-bold text-sm mb-1">
                    {channel.name}
                  </h4>
                  <div className={`text-xs ${getColorClasses(channel.color, 'text')}`}>
                    {channel.subtitle}
                  </div>
                </div>
                
                {/* Effet de brillance au survol */}
                <div 
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.1) 0%, transparent 50%)'
                  }}
                />
              </motion.button>
            ))}
          </div>
          
          {/* Barre de transmission en bas */}
          <div className="mt-6 flex items-center justify-center gap-3 text-cyan-500/50 text-xs font-mono">
            <Waves className="w-4 h-4 animate-pulse" />
            <span>TRANSMISSION CRYPTÉE</span>
            <span className="text-cyan-400/30">|</span>
            <span>LATENCE: 0.3ms</span>
            <span className="text-cyan-400/30">|</span>
            <Zap className="w-4 h-4" />
            <span>CONNEXION SÉCURISÉE</span>
          </div>

          {/* Children slot (chat intégré, etc.) */}
          {children}
        </div>
      </div>
    </motion.div>
  );
};

export default ContactSection;
